import { z } from "zod/v4";
import { AgenticSettingsReviewOpType } from "../../types";

const guidance = z.string().trim().min(1).nullable();
const guidanceChange = z.object({
  reviewed_guidance: z.string().nullable(),
  guidance: guidance.describe(
    "Complete replacement instructions, or null to restore defaults.",
  ),
});
const pixelDimension = z.int().positive().max(2147483647);
const newSize = z.object({
  width: pixelDimension,
  height: pixelDimension,
  label: z.string().trim().min(1).nullable(),
  guidance,
  set_as_default: z.boolean().optional(),
});

export const RESIZE_GUIDANCE_WIRE_OPERATIONS = [
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.CreateChannel),
    name: z.string().trim().min(1),
    guidance,
    sizes: z.array(newSize).min(1),
    reviewed_default_size_id: z.uuid().nullable().optional(),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.UpdateChannel),
    channel_id: z.uuid(),
    rename: z
      .object({ reviewed_name: z.string(), name: z.string().trim().min(1) })
      .optional(),
    guidance: guidanceChange.optional(),
    add_sizes: z.array(newSize).optional(),
    size_guidance: z
      .array(z.object({ channel_size_id: z.uuid(), ...guidanceChange.shape }))
      .optional(),
    reviewed_default_size_id: z.uuid().nullable().optional(),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.SetDefaultChannelSize),
    channel_size_id: z.uuid().nullable(),
    reviewed_default_size_id: z.uuid().nullable(),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.DeleteChannelSize),
    channel_size_id: z.uuid(),
    reviewed_size: z
      .object({
        width: pixelDimension,
        height: pixelDimension,
        label: z.string(),
        guidance: z.string().nullable(),
        is_default: z.boolean(),
        ad_count: z.int().nonnegative(),
      })
      .describe(
        "Copy the size's deletion_snapshot from read_resize_guidance. Approval rejects changed settings or deletion impact.",
      ),
  }),
] as const;
export const RESIZE_GUIDANCE_STORED_OPERATIONS =
  RESIZE_GUIDANCE_WIRE_OPERATIONS;
export type ResizeGuidanceOperation = z.infer<
  (typeof RESIZE_GUIDANCE_STORED_OPERATIONS)[number]
>;

export function resizeGuidanceProposalKey(
  operation: ResizeGuidanceOperation,
): string {
  switch (operation.op_type) {
    case AgenticSettingsReviewOpType.CreateChannel:
      return `${operation.op_type}-${operation.name.toLowerCase()}`;
    case AgenticSettingsReviewOpType.UpdateChannel:
      return `${operation.op_type}-${operation.channel_id}`;
    case AgenticSettingsReviewOpType.SetDefaultChannelSize:
      return operation.op_type;
    case AgenticSettingsReviewOpType.DeleteChannelSize:
      return `${operation.op_type}-${operation.channel_size_id}`;
  }
}
